import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { LocationPromotionRow, OverviewResponse } from '../types';
import {
  CategoryBadge,
  CompositionDonut,
  EmptyPeriodState,
  KpiStrip,
  LocationChips,
  VerdictBadge,
  engineTableCss,
  isEmptyEnginePeriod,
  money,
  pct,
  roi
} from './shared';

const formatWeek = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const OverviewTab = ({ data }: { data: OverviewResponse }) => {
  const currency = data.currency_code || '';
  const topLocations = data.locations.slice(0, 8);
  const [drillLocationId, setDrillLocationId] = useState<number>(
    data.locations[0]?.location_id || 0
  );

  const drillPromos = useMemo<LocationPromotionRow[]>(() => {
    const id = drillLocationId || data.locations[0]?.location_id;
    if (!id) return [];
    return (data.location_promotions || [])
      .filter((row) => row.location_id === id)
      .sort((a, b) => a.margin_impact - b.margin_impact);
  }, [data.location_promotions, data.locations, drillLocationId]);

  const drillLocation = data.locations.find((l) => l.location_id === drillLocationId);

  if (isEmptyEnginePeriod(data.kpis)) {
    return <EmptyPeriodState />;
  }

  const salesTarget = data.targets?.promotional_sales;
  const redemptions = data.targets?.redemptions || [];
  const watch = data.watchlist || {
    margin_loss: [],
    not_kicking: [],
    overperforming: [],
    requires_attention: []
  };

  return (
    <Wrap>
      <KpiStrip kpis={data.kpis} currency={currency} />

      <SectionTitle>Revenue and margin</SectionTitle>
      <Grid2>
        <Panel>
          <PanelHead>
            <h3>Revenue composition</h3>
            <Hint>promotion vs without promotion</Hint>
          </PanelHead>
          <CompositionDonut composition={data.composition} currency={currency} />
        </Panel>
        <Panel>
          <PanelHead>
            <h3>Margin vs discount</h3>
            <Hint>COGS from computed cost</Hint>
          </PanelHead>
          <MetricList>
            <Metric>
              <span>Gross margin (all sales)</span>
              <Mono>{money(data.kpis.gross_margin, currency)}</Mono>
            </Metric>
            <Metric>
              <span>Promo-ticket gross margin</span>
              <Mono className={data.kpis.promo_gross_margin < 0 ? 'neg' : 'pos'}>
                {money(data.kpis.promo_gross_margin, currency)}
              </Mono>
            </Metric>
            <Metric>
              <span>Discount spend</span>
              <Mono>{money(data.kpis.promo_discount, currency)}</Mono>
            </Metric>
            <Metric>
              <span>Promoted items</span>
              <Mono>
                {data.kpis.promoted_items.toLocaleString()} / {data.kpis.sold_items.toLocaleString()}{' '}
                ({pct(data.kpis.item_penetration_pct)})
              </Mono>
            </Metric>
          </MetricList>
          <Formula>{data.roi_formula}</Formula>
        </Panel>
      </Grid2>

      <Grid2>
        <Panel>
          <PanelHead>
            <h3>Weekly promotion penetration</h3>
            <Hint>share of revenue + promo $</Hint>
          </PanelHead>
          {data.trend.length === 0 ? (
            <Empty>No trend points in this range.</Empty>
          ) : (
            <TrendList>
              {data.trend.map((t) => (
                <TrendRow key={t.week_start}>
                  <span>{formatWeek(t.week_start)}</span>
                  <BarTrack>
                    <BarFill style={{ width: `${Math.min(t.penetration_pct, 100)}%` }} />
                  </BarTrack>
                  <TrendVals>
                    <b>{pct(t.penetration_pct)}</b>
                    <em>{money(t.promo_revenue, currency)}</em>
                  </TrendVals>
                </TrendRow>
              ))}
            </TrendList>
          )}
        </Panel>
        <Panel>
          <PanelHead>
            <h3>Engine signals</h3>
          </PanelHead>
          {data.signals.length === 0 ? (
            <Empty>No signals for this period.</Empty>
          ) : (
            data.signals.map((s, idx) => (
              <Signal key={`${s.title}-${idx}`} $tone={s.tone}>
                <strong>{s.title}</strong>
                <p>{s.body}</p>
              </Signal>
            ))
          )}
        </Panel>
      </Grid2>

      <SectionTitle>Promotion performance</SectionTitle>
      <Panel>
        <Table>
          <thead>
            <tr>
              <th>Promotion</th>
              <th>Status</th>
              <th className="num">Orders</th>
              <th className="locs">Locations</th>
              <th className="num">Discount</th>
              <th className="num">Promo rev</th>
              <th className="num">Gross margin</th>
              <th className="num">ROI</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            {data.promotions.length === 0 ? (
              <tr>
                <td colSpan={9}>No promotions in this period.</td>
              </tr>
            ) : (
              data.promotions.map((p) => (
                <tr key={p.promotion_id}>
                  <td>{p.promotion_name}</td>
                  <td>{p.status}</td>
                  <td className="num">{p.orders.toLocaleString()}</td>
                  <td className="locs">
                    <LocationChips names={p.location_names} count={p.location_count} />
                  </td>
                  <td className="num">{money(p.promo_discount, currency)}</td>
                  <td className="num">{money(p.promo_revenue, currency)}</td>
                  <td className={`num ${p.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(p.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(p.promo_roi)}</td>
                  <td>
                    <CategoryBadge categoryKey={p.category_key} label={p.category_label} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Panel>

      <SectionTitle>Promotion by channel</SectionTitle>
      <Panel $scroll>
        <Table>
          <thead>
            <tr>
              <th>Channel</th>
              <th className="num">Revenue</th>
              <th className="num">Mix</th>
              <th className="num">Promo rev</th>
              <th className="num">Penetration</th>
              <th className="num">Discount</th>
              <th className="num">Gross margin</th>
              <th className="num">Margin impact</th>
              <th className="num">ROI</th>
              <th className="num">Orders</th>
            </tr>
          </thead>
          <tbody>
            {data.channels.length === 0 ? (
              <tr>
                <td colSpan={10}>No channel data.</td>
              </tr>
            ) : (
              data.channels.map((c) => (
                <tr key={c.order_channel_id}>
                  <td>{c.order_channel}</td>
                  <td className="num">{money(c.net_revenue, currency)}</td>
                  <td className="num">{pct(c.revenue_contribution_pct)}</td>
                  <td className="num">{money(c.promo_revenue, currency)}</td>
                  <td className="num">{pct(c.penetration_pct)}</td>
                  <td className="num">{money(c.promo_discount, currency)}</td>
                  <td className="num">{money(c.gross_margin, currency)}</td>
                  <td className={`num ${c.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(c.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(c.promo_roi)}</td>
                  <td className="num">{c.orders.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Panel>

      <SectionTitle>Top locations</SectionTitle>
      <Panel>
        {topLocations.length === 0 ? (
          <Empty>No location data.</Empty>
        ) : (
          topLocations.map((l) => (
            <ChannelRow
              key={l.location_id}
              role="button"
              tabIndex={0}
              onClick={() => setDrillLocationId(l.location_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setDrillLocationId(l.location_id);
              }}
            >
              <span>
                {l.location_name}
                <Sub>
                  {pct(l.penetration_pct)} pen · {roi(l.promo_roi)} ROI ·{' '}
                  {money(l.promo_discount, currency)} discount
                </Sub>
              </span>
              <Mono className={l.margin_impact < 0 ? 'neg' : 'pos'}>
                {money(l.margin_impact, currency)}
              </Mono>
            </ChannelRow>
          ))
        )}
      </Panel>

      <SectionTitle>Location → promotion</SectionTitle>
      <Panel>
        <DrillBar>
          <label htmlFor="pe-location-drill">Location</label>
          <Select
            id="pe-location-drill"
            value={drillLocationId || ''}
            onChange={(e) => setDrillLocationId(Number(e.target.value))}
          >
            {data.locations.map((l) => (
              <option key={l.location_id} value={l.location_id}>
                {l.location_name}
              </option>
            ))}
          </Select>
          {drillLocation && (
            <Hint>
              {pct(drillLocation.penetration_pct)} penetration · margin impact{' '}
              {money(drillLocation.margin_impact, currency)}
            </Hint>
          )}
        </DrillBar>
        {drillPromos.length === 0 ? (
          <Empty>No promotions applied at this location in the period.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Promotion</th>
                <th className="num">Orders</th>
                <th className="num">Promo rev</th>
                <th className="num">Discount</th>
                <th className="num">Gross margin</th>
                <th className="num">ROI</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {drillPromos.map((p) => (
                <tr key={`${p.promotion_id}-${p.location_id}`}>
                  <td>{p.promotion_name}</td>
                  <td className="num">{p.orders.toLocaleString()}</td>
                  <td className="num">{money(p.promo_revenue, currency)}</td>
                  <td className="num">{money(p.promo_discount, currency)}</td>
                  <td className={`num ${p.margin_impact < 0 ? 'neg' : 'pos'}`}>
                    {money(p.margin_impact, currency)}
                  </td>
                  <td className="num">{roi(p.promo_roi)}</td>
                  <td>
                    <VerdictBadge verdict={p.verdict} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {(salesTarget || redemptions.length > 0) && (
        <>
          <SectionTitle>Targets and eligibility</SectionTitle>
          <Grid2>
            {salesTarget && (
              <Panel>
                <PanelHead>
                  <h3>{salesTarget.label}</h3>
                  <Hint>{salesTarget.status}</Hint>
                </PanelHead>
                <MetricList>
                  <Metric>
                    <span>Target</span>
                    <Mono>{money(salesTarget.target, currency)}</Mono>
                  </Metric>
                  <Metric>
                    <span>Current promo revenue</span>
                    <Mono>{money(salesTarget.current_performance, currency)}</Mono>
                  </Metric>
                  <Metric>
                    <span>Achievement</span>
                    <Mono>{pct(salesTarget.achievement_pct)}</Mono>
                  </Metric>
                  <Metric>
                    <span>Remaining</span>
                    <Mono>{money(salesTarget.remaining, currency)}</Mono>
                  </Metric>
                </MetricList>
              </Panel>
            )}
            <Panel>
              <PanelHead>
                <h3>Promo code limits</h3>
                <Hint>lifetime vs configured cap</Hint>
              </PanelHead>
              {redemptions.length === 0 ? (
                <Empty>No code redemption limits configured.</Empty>
              ) : (
                redemptions.map((r) => (
                  <ChannelRow key={`${r.promotion_id}-${r.code}`}>
                    <span>
                      {r.promotion_name}
                      <Sub>
                        {r.code} · {r.lifetime_redemptions.toLocaleString()} / {r.target.toLocaleString()}{' '}
                        · {r.remaining.toLocaleString()} remaining · {r.status}
                      </Sub>
                    </span>
                    <Mono>{pct(r.achievement_pct)}</Mono>
                  </ChannelRow>
                ))
              )}
            </Panel>
          </Grid2>
        </>
      )}

      <SectionTitle>Watchlist</SectionTitle>
      <Grid2>
        <Panel>
          <PanelHead>
            <h3>Promotions with margin loss</h3>
          </PanelHead>
          {watch.margin_loss.length === 0 ? (
            <Empty>None this period.</Empty>
          ) : (
            watch.margin_loss.map((p) => (
              <ChannelRow key={`ml-${p.promotion_id}`}>
                <span>{p.promotion_name}</span>
                <Mono className="neg">{money(p.margin_impact, currency)}</Mono>
              </ChannelRow>
            ))
          )}
        </Panel>
        <Panel>
          <PanelHead>
            <h3>Not kicking / outperforming</h3>
          </PanelHead>
          {watch.not_kicking.length === 0 && watch.overperforming.length === 0 ? (
            <Empty>None this period.</Empty>
          ) : (
            <>
              {watch.not_kicking.map((p) => (
                <ChannelRow key={`nk-${p.promotion_id}`}>
                  <span>
                    {p.promotion_name}
                    <Sub>not kicking</Sub>
                  </span>
                  <VerdictBadge verdict={p.verdict} />
                </ChannelRow>
              ))}
              {watch.overperforming.map((p) => (
                <ChannelRow key={`op-${p.promotion_id}`}>
                  <span>
                    {p.promotion_name}
                    <Sub>outperforming — check discount budget</Sub>
                  </span>
                  <VerdictBadge verdict={p.verdict} />
                </ChannelRow>
              ))}
            </>
          )}
        </Panel>
      </Grid2>
    </Wrap>
  );
};

export default OverviewTab;

const Wrap = styled.div``;
const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 14px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const Panel = styled.div<{ $scroll?: boolean }>`
  background: ${({ theme }) => theme.widget_group?.background || '#f4f5f7'};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  border-radius: 14px;
  padding: 18px 20px;
  overflow: ${({ $scroll }) => ($scroll ? 'auto' : 'visible')};
`;
const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  h3 {
    font-size: 1.08rem;
    font-weight: 700;
    margin: 0;
    color: ${({ theme }) => theme.text};
  }
`;
const Hint = styled.span`
  margin-left: auto;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
  font-weight: 600;
`;
const SectionTitle = styled.h2`
  font-size: 0.82rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
  margin: 26px 2px 12px;
  font-weight: 700;
`;
const Table = styled.table`
  ${engineTableCss}
  th:nth-child(2),
  td:nth-child(2) {
    text-align: left;
    font-family: inherit;
  }
  th.locs,
  td.locs {
    padding-left: 24px;
  }
`;
const Sub = styled.span`
  display: block;
  font-size: 0.74rem;
  color: ${({ theme }) => theme.text_muted || theme.text};
  font-weight: 400;
  margin-top: 2px;
`;
const Empty = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;
const TrendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const TrendRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr minmax(140px, auto);
  gap: 10px;
  align-items: center;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.text};
`;
const TrendVals = styled.div`
  text-align: right;
  b {
    display: block;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
  em {
    font-style: normal;
    font-size: 0.78rem;
    color: ${({ theme }) => theme.text_muted || theme.text};
  }
`;
const BarTrack = styled.div`
  height: 10px;
  border-radius: 4px;
  background: ${({ theme }) => theme.border || 'rgba(0,0,0,0.08)'};
  overflow: hidden;
`;
const BarFill = styled.div`
  height: 100%;
  background: #ff5a2d;
`;
const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.88rem;
  text-align: right;
  &.pos {
    color: #16a34a;
  }
  &.neg {
    color: #e11d48;
  }
`;
const ChannelRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.06)'};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text};
  &:last-child {
    border-bottom: 0;
  }
`;
const Signal = styled.div<{ $tone: string }>`
  padding: 11px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.08)'};
  margin-bottom: 8px;
  background: ${({ $tone }) =>
    $tone === 'warn'
      ? 'rgba(225,29,72,0.06)'
      : $tone === 'caut'
        ? 'rgba(234,88,12,0.06)'
        : 'rgba(22,163,74,0.06)'};
  strong {
    display: block;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.text};
  }
  p {
    margin: 4px 0 0;
    font-size: 0.82rem;
    color: ${({ theme }) => theme.text_secondary || theme.text};
  }
`;
const MetricList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const Metric = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text};
`;
const Formula = styled.p`
  margin: 14px 0 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
`;
const DrillBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  label {
    font-size: 0.82rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
  }
`;
const Select = styled.select`
  min-width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  background: ${({ theme }) => theme.widget.background};
  color: ${({ theme }) => theme.text};
  font-size: 0.9rem;
`;
